import React, { useState, useEffect, useCallback } from 'react';
import { Webhook as WebhookIcon, Plus, Trash2, Copy, Check } from 'lucide-react';
import { webhooksAPI } from '../services/api';
import Button from './common/Button';
import Alert from './common/Alert';
import LoadingSpinner from './common/LoadingSpinner';

const ALL_EVENTS = ['link.created', 'link.clicked'];

const Webhooks = () => {
  const [hooks, setHooks] = useState(null);
  const [error, setError] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [url, setUrl] = useState('');
  const [events, setEvents] = useState(['link.clicked']);
  const [creating, setCreating] = useState(false);
  const [newSecret, setNewSecret] = useState(null);
  const [copied, setCopied] = useState(false);

  const load = useCallback(async () => {
    const r = await webhooksAPI.list();
    if (r.success) setHooks(r.data.webhooks);
    else setError(r.message || 'Failed to load webhooks');
  }, []);

  useEffect(() => { load(); }, [load]);

  const toggleEvent = (e) =>
    setEvents((prev) => (prev.includes(e) ? prev.filter((x) => x !== e) : [...prev, e]));

  const create = async () => {
    setCreating(true);
    setError('');
    const r = await webhooksAPI.create({ url, events });
    setCreating(false);
    if (r.success) {
      setNewSecret(r.data.secret);
      setShowForm(false);
      setUrl('');
      setEvents(['link.clicked']);
      load();
    } else {
      setError(r.message || 'Failed to create webhook');
    }
  };

  const toggleActive = async (h) => {
    await webhooksAPI.update(h.id, { isActive: !h.is_active });
    load();
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this webhook?')) return;
    await webhooksAPI.remove(id);
    load();
  };

  return (
    <div className="max-w-4xl mx-auto">
      <header className="flex items-end justify-between gap-4 mb-8">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-ink-900">Webhooks</h1>
          <p className="mt-1.5 text-sm text-ink-500">Get notified when your links are created or clicked.</p>
        </div>
        <Button onClick={() => { setShowForm((v) => !v); setNewSecret(null); }}>
          <Plus className="h-4 w-4" /> New webhook
        </Button>
      </header>

      {error && <Alert type="error" message={error} className="mb-5" onClose={() => setError('')} />}

      {newSecret && (
        <div className="mb-5 rounded-xl border border-emerald-200 bg-emerald-50 p-4">
          <p className="text-sm font-medium text-emerald-900">Webhook created. Save this signing secret — it won't be shown again:</p>
          <div className="mt-2 flex items-center gap-2">
            <code className="flex-1 font-mono text-xs bg-white border border-emerald-200 rounded px-3 py-2 break-all">{newSecret}</code>
            <Button variant="secondary" size="small" onClick={() => { navigator.clipboard.writeText(newSecret); setCopied(true); setTimeout(() => setCopied(false), 1500); }}>
              {copied ? <Check className="h-4 w-4 text-emerald-600" /> : <Copy className="h-4 w-4" />}
            </Button>
          </div>
          <p className="mt-2 text-xs text-emerald-700">Requests are signed with HMAC-SHA256 in the <code>X-Dynalink-Signature</code> header.</p>
        </div>
      )}

      {showForm && (
        <div className="mb-6 rounded-2xl border border-ink-200 bg-white p-6 shadow-soft space-y-4">
          <div>
            <label className="block text-sm font-medium text-ink-700 mb-1">Endpoint URL</label>
            <input
              type="url"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder="https://your-app.com/webhooks/dynalink"
              className="w-full rounded-lg border border-ink-200 px-3 py-2.5 text-sm focus:outline-none focus:ring-4 focus:ring-brand-100 focus:border-brand-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-ink-700 mb-2">Events</label>
            <div className="flex flex-wrap gap-3">
              {ALL_EVENTS.map((e) => (
                <label key={e} className="inline-flex items-center gap-2 text-sm">
                  <input type="checkbox" checked={events.includes(e)} onChange={() => toggleEvent(e)} className="rounded border-ink-300 text-brand-600" />
                  <code>{e}</code>
                </label>
              ))}
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={() => setShowForm(false)}>Cancel</Button>
            <Button onClick={create} loading={creating} disabled={!url || events.length === 0}>Create</Button>
          </div>
        </div>
      )}

      {hooks === null ? (
        <LoadingSpinner text="Loading webhooks…" />
      ) : hooks.length === 0 ? (
        <div className="text-center py-12 rounded-2xl border border-ink-200 bg-white">
          <WebhookIcon className="mx-auto h-10 w-10 text-ink-300" />
          <p className="mt-3 text-sm text-ink-500">No webhooks yet.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {hooks.map((h) => (
            <div key={h.id} className="flex items-center justify-between gap-4 rounded-xl border border-ink-200 bg-white p-4 shadow-soft">
              <div className="min-w-0">
                <p className="font-mono text-sm text-ink-900 truncate">{h.url}</p>
                <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-ink-500">
                  {(h.events || []).map((e) => (
                    <span key={e} className="rounded-full bg-ink-100 px-2 py-0.5">{e}</span>
                  ))}
                  {h.last_status != null && <span>· last delivery: {h.last_status || 'failed'}</span>}
                </div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <button
                  onClick={() => toggleActive(h)}
                  className={`text-xs font-medium rounded-full px-2.5 py-1 ${h.is_active ? 'bg-emerald-100 text-emerald-700' : 'bg-ink-100 text-ink-500'}`}
                >
                  {h.is_active ? 'Active' : 'Paused'}
                </button>
                <button onClick={() => remove(h.id)} className="inline-flex h-8 w-8 items-center justify-center rounded-md text-ink-500 hover:text-rose-600 hover:bg-rose-50">
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default Webhooks;
